import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { API, formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, ArrowLeft, RotateCcw } from "lucide-react";

const DEFAULT_POSITIONS = {
  photo_box: [72, 108, 660, 720],
  message_box: [786, 162, 1725, 450],
  doctor_block_box: [786, 492, 1725, 768],
  message_font_size: 39,
  doctor_name_font_size: 66,
  doctor_meta_font_size: 39,
  message_color: [255, 255, 255],
  doctor_name_color: [255, 255, 255],
  doctor_meta_color: [255, 255, 255],
};

const FIELDS = [
  ["photo_box", "Photo Box (x1, y1, x2, y2)"],
  ["message_box", "Message Box"],
  ["doctor_block_box", "Doctor Block Box"],
];

const SCALARS = [
  ["message_font_size", "Message Font Size"],
  ["doctor_name_font_size", "Doctor Name Font Size"],
  ["doctor_meta_font_size", "Doctor Meta Font Size"],
];

export default function AdminPositions() {
  const { id } = useParams();
  const [tpl, setTpl] = useState(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      const t = data.find((x) => x.id === id);
      setTpl(t); setPos(t?.positions);
    });
  }, [id]);

  const save = async () => {
    try {
      await api.patch(`/admin/templates/${id}`, { positions: pos });
      toast.success("Positions saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  if (!tpl || !pos) return <Shell role="admin"><div className="text-slate-500">Loading…</div></Shell>;

  return (
    <Shell role="admin">
      <Link to="/admin/templates" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3"><ArrowLeft className="w-4 h-4"/>Back to templates</Link>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mb-1">Position Configuration</h1>
      <p className="text-slate-500 mb-6">{tpl.name}</p>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="space-y-4">
          {FIELDS.map(([k, l]) => (
            <Card key={k} className="p-5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {pos[k].map((v, i) => (
                  <Input key={i} type="number" value={v} onChange={(e) => {
                    const arr = [...pos[k]]; arr[i] = parseInt(e.target.value || 0, 10); setPos({ ...pos, [k]: arr });
                  }} data-testid={`pos-${k}-${i}`}/>
                ))}
              </div>
            </Card>
          ))}
          <Card className="p-5 grid grid-cols-3 gap-3">
            {SCALARS.map(([k, l]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
                <Input type="number" value={pos[k]} onChange={(e) => setPos({ ...pos, [k]: parseInt(e.target.value || 0, 10) })} data-testid={`pos-${k}`}/>
              </div>
            ))}
          </Card>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPos({ ...DEFAULT_POSITIONS })} className="h-12 gap-2 flex-1" data-testid="pos-reset">
              <RotateCcw className="w-4 h-4"/>Reset to Default
            </Button>
            <Button onClick={save} className="h-12 bg-brand-800 hover:bg-brand-700 gap-2 flex-1" data-testid="pos-save">
              <Save className="w-4 h-4"/>Save Positions
            </Button>
          </div>
        </div>

        <Card className="p-3 canvas-checker self-start lg:sticky lg:top-6">
          <div className="relative bg-white rounded">
            <img src={`${API}/templates/${id}/file`} alt={tpl.name} className="w-full block rounded" />
            {/* Overlay rectangles */}
            <Overlay box={pos.photo_box} color="rgba(56,189,248,.6)" label="Photo"/>
            <Overlay box={pos.message_box} color="rgba(34,197,94,.6)" label="Message"/>
            <Overlay box={pos.doctor_block_box} color="rgba(59,130,246,.6)" label="Doctor"/>
          </div>
        </Card>
      </div>
    </Shell>
  );
}

const Overlay = ({ box, color, label }) => {
  const [x1, y1, x2, y2] = box;
  const W = 1788, H = 2526;
  return (
    <div
      className="absolute border-2 rounded-sm pointer-events-none flex items-start justify-end"
      style={{
        left: `${(x1 / W) * 100}%`,
        top: `${(y1 / H) * 100}%`,
        width: `${((x2 - x1) / W) * 100}%`,
        height: `${((y2 - y1) / H) * 100}%`,
        borderColor: color,
        background: color.replace(/, ?[\d.]+\)/, ", .12)"),
      }}
    >
      <span className="text-[10px] px-1.5 py-0.5 m-1 bg-black/60 text-white rounded">{label}</span>
    </div>
  );
};
